// Keyboard Shortcuts Reference Modal
// Displays available keyboard shortcuts for accessibility (activated with "?" key)
// WCAG 2.1 AA compliant with focus management

import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import FocusLock from 'react-focus-lock'
import { useFocusTrap } from '@/hooks/useFocusTrap'

export interface KeyboardShortcut {
  keys: string[]
  description: string
  category?: string
}

const defaultShortcuts: KeyboardShortcut[] = [
  // Calendar Navigation
  { keys: ['←', '→'], description: 'Navigate calendar by day', category: 'Calendar' },
  { keys: ['↑', '↓'], description: 'Navigate calendar by week', category: 'Calendar' },
  { keys: ['j', 'k'], description: 'Previous/next day (vim-style)', category: 'Calendar' },
  { keys: ['h', 'l'], description: 'Previous/next week (vim-style)', category: 'Calendar' },
  { keys: ['Enter'], description: 'Activate/create event on focused date', category: 'Calendar' },

  // General Navigation
  { keys: ['Tab'], description: 'Move to next interactive element', category: 'General' },
  { keys: ['Shift', 'Tab'], description: 'Move to previous interactive element', category: 'General' },
  { keys: ['Escape'], description: 'Close modal or cancel operation', category: 'General' },
  { keys: ['?'], description: 'Show this keyboard shortcuts dialog', category: 'General' },

  // Accessibility
  { keys: ['Alt', 'S'], description: 'Skip to main content', category: 'Accessibility' },
]

interface KeyboardShortcutsModalProps {
  isOpen: boolean
  onClose: () => void
  additionalShortcuts?: KeyboardShortcut[]
}

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
  additionalShortcuts = [],
}) => {
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)

  // Focus trap management
  useFocusTrap({
    isActive: isOpen,
    onEscapePress: onClose,
    initialFocusRef: closeButtonRef,
    returnFocus: true,
  })

  const allShortcuts = [...defaultShortcuts, ...additionalShortcuts]

  // Group shortcuts by category
  const groupedShortcuts = allShortcuts.reduce((acc, shortcut) => {
    const category = shortcut.category || 'Other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(shortcut)
    return acc
  }, {} as Record<string, KeyboardShortcut[]>)

  if (!isOpen) return null

  return (
    <div
      className="modal-overlay"
      aria-modal="true"
      role="dialog"
      aria-labelledby="keyboard-shortcuts-title"
      onClick={onClose}
    >
      <FocusLock returnFocus>
        <div
          className="modal-content max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2
              id="keyboard-shortcuts-title"
              className="text-2xl font-display font-semibold text-white"
            >
              Atalhos de Teclado
            </h2>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className="btn-icon text-brand-gray hover:text-brand-primary"
              aria-label="Fechar diálogo de atalhos"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Shortcuts Grid */}
          <div className="space-y-6">
            {Object.entries(groupedShortcuts).map(([category, shortcuts]) => (
              <div key={category}>
                <h3 className="text-sm font-display font-semibold text-brand-primary uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="keyboard-shortcuts-grid">
                  {shortcuts.map((shortcut, index) => (
                    <div key={index} className="keyboard-shortcut-item">
                      <div className="keyboard-shortcut-keys">
                        {shortcut.keys.map((key, keyIndex) => (
                          <React.Fragment key={keyIndex}>
                            <kbd className="keyboard-shortcut-key">{key}</kbd>
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className="text-brand-gray">+</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <span className="keyboard-shortcut-description">
                        {shortcut.description}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-700">
            <p className="text-sm text-brand-gray text-center">
              Pressione <kbd className="keyboard-shortcut-key">?</kbd> a qualquer momento para exibir
              este diálogo novamente.
            </p>
          </div>
        </div>
      </FocusLock>
    </div>
  )
}

/**
 * Hook to manage keyboard shortcuts modal
 * Automatically opens modal when "?" is pressed
 */
export function useKeyboardShortcuts(additionalShortcuts?: KeyboardShortcut[]) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Check for "?" key (Shift + /)
      if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // Don't open if user is typing in an input
        if (
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLTextAreaElement ||
          (event.target instanceof HTMLElement && event.target.isContentEditable)
        ) {
          return
        }

        event.preventDefault()
        setIsOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyPress)

    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [])

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    KeyboardShortcutsModal: (
      <KeyboardShortcutsModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        additionalShortcuts={additionalShortcuts}
      />
    ),
  }
}
